import type { ClientBankAccountsLists, PaasTransaction, ClientBalanceResponse, VerifyInteracRequest } from 'vopay';

import axios from 'axios';
import vopayApi from '../services/api.config';
import { useDataFetchIndicator } from '.';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';

const { api } = vopayApi;

export type VerificationResponse = {
  status:
    | 'VERIFICATION_UNAVAILABLE'
    | 'VERIFICATION_SEND_SUCCESS'
    | 'VERIFICATION_LINK_SUCCESS'
    | 'VERIFICATION_FAIL'
    | 'VERIFICATION_ELINX_SETTINGS_UNAVAILABLE';
  message?: string;
  link?: string;
};

export type TransactionData = {
  transactions: PaasTransaction[];
  lastRetrievedTime: string;
};

export type BalanceData = {
  balance: ClientBalanceResponse['AccountBalance'];
  asOfDate: ClientBalanceResponse['BalanceAsAtDate'];
};

export type BankAccountDetails = {
  account: ClientBankAccountsLists;
  balanceData: BalanceData;
  transactionData: TransactionData | null;
};

type HookProps = {
  accountID: string;
  email: string;
};

export default function usePaymentMethodDetails({ accountID, email }: HookProps) {
  const { response: loginUser } = useClientData();
  const loginUserName = loginUser.LoginUserName;
  const source = axios.CancelToken.source();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { permissions } = usePermissions();
  const { PayLinkEnabled, ClientAccountsEnabled } = useLockedFeatures();

  async function fetchBankAccountDetails(token: string): Promise<BankAccountDetails> {
    try {
      setDataFetchingInfo(true, 'fetching bank account info', 'fullScreen');

      const [account, balanceData, transactionData] = await Promise.all([
        fetchBankAccount(token),
        fetchBalanceData(token),
        fetchTransactionData(token),
      ]);

      return {
        account,
        balanceData,
        transactionData,
      };
    } finally {
      setDataFetchingInfo(false);
    }
  }

  async function fetchBankAccount(token: string) {
    const res = await api.fetchClientBankAccounts(
      {
        PortalUser: loginUserName,
        Token: token,
        ClientAccountID: accountID,
      },
      source,
    );

    return res.BankAccounts[0];
  }

  async function fetchTransactionData(Token: string) {
    const res = await api.fetchClientTransactions(
      {
        PortalUser: loginUserName,
        Token,
      },
      source,
    );

    if (res.Success && res.AggregatorTransactions.length > 0) {
      return {
        transactions: res.AggregatorTransactions,
        lastRetrievedTime: res.LastRetrievedTime,
      };
    }

    return null;
  }

  async function fetchBalanceData(Token: string): Promise<BalanceData> {
    const res = await api.fetchClientBalance({
      PortalUser: loginUserName,
      Token,
    });

    return {
      balance: res.AccountBalance,
      asOfDate: res.BalanceAsAtDate,
    };
  }

  const verifyInteracAccount = async (interacData: VerifyInteracRequest) => {
    try {
      setDataFetchingInfo(true, 'setting interac default payment method', 'fullScreen');

      const basePayload = {
        EmailAddress: interacData.EmailAddress,
        VerificationTier: interacData.VerificationTier,
      };
      const payload = ClientAccountsEnabled ? { ClientAccountID: accountID } : { ContactID: interacData.ContactID };

      const verificationResponse = await api.verifyInterac({ ...payload, ...basePayload }, source);
      if (!verificationResponse.Success) {
        throw new Error(verificationResponse.ErrorMessage);
      }
      return verificationResponse;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const verifyCardAccount = async (cardToken: string, verificationTier: 'basic' | 'enhanced') => {
    try {
      setDataFetchingInfo(true, 'verifying card account', 'fullScreen');

      // TODO: Implement the actual API call once the endpoint is ready
      const verificationResponse = await api.verifyCard(
        {
          CardToken: cardToken,
          VerificationTier: verificationTier,
        },
        source,
      );
      if (!verificationResponse.Success) {
        throw new Error(verificationResponse.ErrorMessage);
      }

      return verificationResponse;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const verifyBankAccount = async (bankAccount: ClientBankAccountsLists): Promise<VerificationResponse> => {
    try {
      setDataFetchingInfo(true, 'setting default payment method', 'fullScreen');

      if (!permissions.ViewElinxSettings || !PayLinkEnabled) {
        return {
          status: 'VERIFICATION_ELINX_SETTINGS_UNAVAILABLE',
        };
      }

      const paylinkSettingsResponse = await api.fetchPaylinkSettings(
        {
          PortalUser: loginUserName,
        },
        source,
      );

      const senderEmail = paylinkSettingsResponse.ELinxSettings?.ELinxSenderEmailAddress;

      if (!senderEmail) {
        return {
          status: 'VERIFICATION_UNAVAILABLE',
        };
      }

      const shouldSendEmail = senderEmail !== '' && email !== '';

      const verificationResponse = await api.initiateBankAccountVerification(
        {
          Country: bankAccount.Currency === 'CAD' ? 'CA' : 'US',
          VerificationMode: 'credit',
          FirstName:
            bankAccount.AccountHolderName?.split(' ').length >= 2 ? bankAccount.AccountHolderName?.split(' ')[0] : '',
          LastName:
            bankAccount.AccountHolderName?.split(' ').length >= 2 ? bankAccount.AccountHolderName?.split(' ')[1] : '',
          SendEmail: shouldSendEmail,
          ReceiverEmailAddress: email,
          SenderEmailAddress: senderEmail,
          ClientRefNumber: bankAccount.ClientReferenceNumber,
          AccountNumber: bankAccount.AccountNumber,
          InstitutionNumber: bankAccount.FinancialInstitutionNumber,
          TransitNumber: bankAccount.BranchTransitNumber,
          CompanyName:
            bankAccount.AccountHolderName?.split(' ').length < 2 ? bankAccount.AccountHolderName?.split(' ')[0] : '',
          IntegrationType: bankAccount.IntegrationType,
          FlinksAccountID: bankAccount.FlinksAccountID,
          FlinksLoginID: bankAccount.FlinksLoginID,
          PlaidAccountID: bankAccount.PlaidAccountID,
          PlaidProcessorToken: bankAccount.PlaidProcessorToken,
          InveriteRequestGUID: bankAccount.InveriteRequestGUID,
          MxAuthorizationCode: bankAccount.MxAuthorizationCode,
          FiservToken: bankAccount.FiservToken,
          MxProcessorToken: bankAccount.MxProcessorToken,
          Token: bankAccount.Token,
          FlinksAccountId: bankAccount.FlinksAccountID,
          FlinksLoginId: bankAccount.FlinksLoginID,
        },
        source,
      );

      if (!verificationResponse.Success) {
        return {
          status: 'VERIFICATION_FAIL',
          message: verificationResponse.ErrorMessage,
        };
      }

      const link = verificationResponse.Link;

      if (shouldSendEmail) {
        return {
          status: 'VERIFICATION_SEND_SUCCESS',
          link,
        };
      }

      return {
        status: 'VERIFICATION_LINK_SUCCESS',
        link,
      };
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const fetchInteracDetails = async (token: string) => {
    try {
      setDataFetchingInfo(true, 'setting getting interac details', 'fullScreen');

      const res = await api.fetchInteracEmail(
        {
          ClientAccountID: accountID,
        },
        source,
      );

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return Object.values(res.InteracEmails).find((e) => e.Token === token);
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const fetchCreditCardDetails = async (CreditCardToken: string) => {
    try {
      setDataFetchingInfo(true, 'setting getting credit card details', 'fullScreen');

      const res = await api.fetchCreditCards({
        CreditCardToken,
        ClientAccountID: accountID,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res.CreditCards[0];
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const fetchDebitCardDetails = async (DebitCardToken: string) => {
    try {
      setDataFetchingInfo(true, 'setting getting credit card details', 'fullScreen');

      const res = await api.fetchDebitCards({
        DebitCardToken,
        ClientAccountID: accountID,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res.DebitCards[0];
    } finally {
      setDataFetchingInfo(false);
    }
  };

  return {
    fetchBankAccountDetails,
    fetchTransactionData,
    verifyBankAccount,
    verifyInteracAccount,
    verifyCardAccount,
    fetchInteracDetails,
    fetchCreditCardDetails,
    fetchDebitCardDetails,
    fetchBalanceData,
  };
}
